import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  getCustomFilters,
  getCustomViews,
  getDefaultHeaders,
  getSelectedView,
} from '../selectors';
import { filter, map, Observable } from 'rxjs';
import {
  GridType,
  GridView,
  GridViewCustomFilters,
  GridViewSelect,
} from '../types';
import { GRID_VIEW_DATA_ACTIONS, GRID_VIEW_TABLE_ACTIONS } from '../actions';
import { HeaderCellConfiguration } from '@wcg/frontend-framework';
import { GRID_VIEW_FEATURE_STATE } from '@data-access/grid-views';
@Injectable({
  providedIn: 'root',
})
export class GridViewFacadeService {
  private readonly _store = inject(Store);
  /**
   * @toSignals()
   * */
  private _$gType = toSignal(
    this._store.select(GRID_VIEW_FEATURE_STATE.selectGridType).pipe(
      filter(Boolean),
      map((gType) => gType),
    ),
  ) as Signal<GridType>;

  private _$total = toSignal(
    this._store.select(getCustomViews).pipe(
      filter(Boolean),
      map((d) => d[this._$gType()]?.total),
    ),
  ) as Signal<number>;

  private _$gridviewData = toSignal(
    this._store.select(getCustomViews).pipe(
      filter(Boolean),
      map((grid) => grid[this._$gType()]?.data),
    ),
  ) as Signal<GridView[]>;

  private _selectedView = toSignal(
    this._store.select(getSelectedView).pipe(
      filter(Boolean),
      map((view: GridViewSelect) => view),
    ),
  ) as Signal<GridViewSelect>;

  // private _viewOptions = signal<GridViewSelect[]>()

  /**
   * @error handling
   * */
  gridViewSaveFailure = this._store.select(getCustomViews).pipe(
    filter(Boolean),
    map((g) => g[this._$gType()]?.error),
  ) as Observable<string>;

  /**
   * @Computed
   * */
  $totalGridViews = computed(() => this._$total());
  $gridViewData = computed(() => this._$gridviewData());
  $currentGridView = computed(() => {
    if (!(this._selectedView()?.label.indexOf('Default') > -1)) {
      return this._$gridviewData()?.reduce((grid, arr) => {
        if (arr.id === this._selectedView()?.id) {
          grid = { ...arr };
        }
        return grid;
      }, {} as GridView);
    }
    return {} as GridView;
  });

  /**
   * @Component Communication
   * */
  $selectedView = computed(() => this._selectedView()!);
  $defaultHeaders = toSignal(
    this._store.select(getDefaultHeaders).pipe(map((m) => m)),
  ) as Signal<HeaderCellConfiguration[]>;

  $customFilters = toSignal(
    this._store.select(getCustomFilters).pipe(map((filters) => filters)),
  ) as Signal<GridViewCustomFilters>;

  resetSelectedView(): void {
    this._store.dispatch(
      GRID_VIEW_DATA_ACTIONS.setCustomViewSelection({
        selectedView: {
          id: 'Default',
          label: 'Default View',
        },
      }),
    );
  }

  setDefaultHeaders(headers: HeaderCellConfiguration[]) {
    this._store.dispatch(
      GRID_VIEW_TABLE_ACTIONS.saveDefaultTableHeaders({
        headers: headers,
      }),
    );
  }
}
